import { injectable } from "inversify";
import { ValidationService } from ".";
import { EventConstants } from "../EventConstants";
import { doWhen } from "../functions/do-when";
import { isNullOrUndefined } from "../functions/is-null-or-undefined";
import { ServiceBase } from "./ServiceBase";

/**
 * JQuery validation services, used in conjunction with JQuery unobtrusive validation.
 * This class is used to apply validation modifications and to re-initialise validation and to manually trigger validation.
 */
@injectable()
export class JQueryValidationService extends ServiceBase implements ValidationService {

	public static readonly validatorData = "validator";

	/**
	 * Validation modifier functions, used to apply/reapply validators.
	 */
	private validationModifiers = new Array<Function>
	(
		this.applyEmailInputValidation,
		this.applyMinPasswordLength,
		this.applyNotEqual,
		this.applyNumericInputValidation,
		this.applyDateValidation
	);

	/**
	 * @inheritdoc
	 */
	public applyValidation(form: HTMLFormElement): void {

		const jForm = $(form);

		// jquery validate modifications
		if ($.validator) {

			// execute validation modifiers
			this.validationModifiers.forEach(modifier => modifier());

			const doHandler = () => {

				const validator = jForm.data(JQueryValidationService.validatorData) as JQueryValidation.Validator;

				// disable the onkeyup validation, this improves usability and validate-ability
				if (validator) {

					validator.settings.ignore = ":hidden, .MuiInputBase-input";
					validator.settings.onkeyup = false;
					validator.settings.onfocusout = function (element) {
						validator.element($(element));
					}

					// apply on invalid handler
					jForm.off("invalid-form.validate")
						.on("invalid-form.validate", this.onInvalid);
				}
			}

			const whenHandler = () => {

				return !isNullOrUndefined(jForm.data(JQueryValidationService.validatorData));
			};

			// execute the following code with a doWhen to make sure jquery unobstrusive validation has run first
			doWhen(doHandler, whenHandler);
		}
	}

	/**
	 * @inheritdoc
	 */
	public isValidForm(form: HTMLFormElement) {

		const jForm = $(form);
		// true if valid or if there is no validator setup
		return !jForm.data(JQueryValidationService.validatorData) || jForm.valid() === true;
	}

	/**
	 * @inheritdoc
	 */
	public reapplyUnobtrusiveValidator(form: HTMLFormElement) {

		if ($.validator?.unobtrusive) {

			const jForm = $(form as HTMLElement);
			jForm.removeData(JQueryValidationService.validatorData).removeData("unobtrusiveValidation");
			$.validator.unobtrusive.parse(document.body);
		}
	}

	private applyEmailInputValidation() {

		// the following email validation is the same as the default query validation but requiring .com, .co etc suffix
		$.validator.methods["email"] = function (this: JQueryValidation.RulesDictionary, value: string, element: HTMLInputElement) {
			return this.optional(element) || /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(value);
		};
	}

	private applyMinPasswordLength() {

		$.validator.addMethod("password-min-length",
			(value, element) => {
				const minLength = parseInt(element.getAttribute("min-length") ?? "0");
				return value.length >= minLength;
			}
		);

		$.validator.unobtrusive.adapters.addBool("password-min-length");
	}

	private applyNotEqual() {

		$.validator.addMethod("not-equal",
			(value, element) => {

				let notEqualField = element.getAttribute("data-val-not-equal-field");
				const inputElement = element as HTMLInputElement;
				const fieldName = inputElement?.name;

				// check for a deep path, this code will only work for properties on the same model
				if (fieldName.indexOf(".") > -1) {

					const parentPath = fieldName.substring(0, fieldName.lastIndexOf(".") + 1);
					notEqualField = parentPath + notEqualField;
				}

				// get the other element to compare values
				const notEqualInputElement = element.closest("form")?.querySelector(`[name="${notEqualField}"]`) as HTMLInputElement;

				if (notEqualInputElement && value || notEqualInputElement.value) {

					// as soon as this field is validateable, add change event onto the other field 
					// to make it trigger blur on this field and ensure validation is kept in sync
					$(notEqualInputElement).off(EventConstants.change_ipfvalidator).on(EventConstants.change_ipfvalidator, () => {

						$(element).trigger(EventConstants.blur);
					});

					// valid if the values are not equal
					return notEqualInputElement.value !== inputElement.value;
				}

				return true;
			}
		);

		$.validator.unobtrusive.adapters.addBool("not-equal");
	}

	private applyNumericInputValidation() {

		// the following number validation enables input with thousands comma separators
		$.validator.methods["number"] = function (this: JQueryValidation.RulesDictionary, value: string, element: HTMLInputElement) {
			return this.optional(element) || /^(?:-?\d+|-?\d{1,3}(?:,\d{3})+)?(?:\.\d+)?$/.test(value);
		};

		// the following number validation enables input with thousands comma separators
		$.validator.methods["range"] = function (this: JQueryValidation.RulesDictionary, value: string, element: HTMLInputElement, param: Array<number>) {
			const numValue = parseFloat(value.replace(/[^0-9\.]+/g, ""));
			return this.optional(element) || (numValue >= param[0] && numValue <= param[1]);
		};
	}

	private applyDateValidation() {

		$.validator.addMethod("min-date",
			(value, element) => {

				const minDateStr = element.getAttribute("data-val-min-date-value");

				if (!minDateStr) {
					return true;
				}

				const minDate = new Date(minDateStr);
				const date = new Date(value);
				return date.getTime() >= minDate.getTime();
			}
		);

		$.validator.unobtrusive.adapters.addBool("min-date");

		$.validator.addMethod("max-date",
			(value, element) => {

				const maxDateStr = element.getAttribute("data-val-max-date-value");

				if (!maxDateStr) {
					return true;
				}

				const maxDate = new Date(maxDateStr);
				const date = new Date(value);
				return date.getTime() <= maxDate.getTime();
			}
		);

		$.validator.unobtrusive.adapters.addBool("max-date");
	}

	private onInvalid() {

		// ensure react rendered input controls are highlighted
		document.querySelectorAll<HTMLInputElement>(".invisible-input.input-validation-error").forEach(element => {
			const group = element.closest(".input-group");
			const component = group?.querySelector(".react-component");

			if (!component) {
				return;
			}

			const reactInput = component.querySelector(".MuiInputBase-input, .form-control:not(.invisible-input)");
			if (reactInput && !reactInput.classList.contains("input-validation-error")) {
				reactInput.classList.add("input-validation-error");
			}
		});
	}
}